import React from 'react';
import { Link } from 'react-router-dom';
import { 
  PlaneIcon, 
  TrophyIcon, 
  ShieldIcon, 
  StarIcon, 
  GlobeIcon, 
  ClockIcon,
  CheckIcon,
  PhoneIcon,
  EmailIcon,
  WhatsAppIcon,
  MapPinIcon,
  TargetIcon,
  LockIcon,
  CreditCardIcon
} from '../components/Icons';
import SEO from '../components/SEO';

const About = () => {
  const stats = [
    { number: '5+', label: 'Years Experience', icon: ClockIcon },
    { number: '1K+', label: 'Happy Travelers', icon: PlaneIcon },
    { number: '100+', label: 'Destinations', icon: GlobeIcon },
    { number: '98%', label: 'Satisfaction Rate', icon: StarIcon }
  ];

  const values = [
    {
      icon: TargetIcon,
      title: 'Our Mission',
      description: 'To make travel accessible, enjoyable, and memorable for everyone by providing exceptional service, competitive prices, and personalized experiences that create lasting memories.'
    },
    {
      icon: StarIcon,
      title: 'Our Vision',
      description: 'To be India\'s most trusted and preferred travel partner, known for excellence, innovation, and commitment to creating extraordinary travel experiences.'
    },
    {
      icon: ShieldIcon,
      title: 'Our Values',
      description: 'We believe in integrity, transparency, customer-first approach, and sustainable travel. Every journey with us is built on trust, respect, and genuine care for our travelers.'
    }
  ];

  const features = [
    { icon: CheckIcon, text: '5+ years of travel industry experience' },
    { icon: CheckIcon, text: 'Expert team of travel consultants' },
    { icon: CheckIcon, text: '100+ destinations across India' },
    { icon: CheckIcon, text: '1K+ satisfied travelers' },
    { icon: CheckIcon, text: 'Best price guarantee' },
    { icon: CheckIcon, text: '24/7 customer support' },
    { icon: CheckIcon, text: 'Customized travel packages' },
    { icon: CheckIcon, text: 'Safe and secure bookings' }
  ];

  return (
    <div className="flex-1">
      <SEO
        title="About Us"
        description="Happy Yatra Travels - Your trusted travel partner with 5+ years of experience. 1K+ happy travelers, 100+ destinations. Mission, vision, and values that drive us."
        keywords="about Happy Yatra Travels, travel agency about, trusted travel partner, India travel company"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-blue via-dark-blue to-primary-blue overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7z' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/svg%3E")`,
              backgroundSize: '50px 50px'
            }}
          ></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
            <TrophyIcon className="w-5 h-5 text-yellow-300" />
            <span className="text-white font-semibold text-sm">Premium Travel Partner</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight text-white">
            About Happy Yatra Travels
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Your trusted travel partner for unforgettable journeys across India
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const StatIcon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center">
                      <StatIcon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-primary-blue mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-semibold">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Happy Yatra Travels was founded with a simple yet powerful vision: to make travel accessible, enjoyable, and memorable for everyone. With over 5+ years of experience in the travel industry, we have been dedicated to creating extraordinary travel experiences across India.
                </p>
                <p>
                  What started as a small travel agency has grown into a trusted name in the travel industry, serving thousands of satisfied travelers. Our journey has been marked by a commitment to excellence, personalized service, and an unwavering passion for travel.
                </p>
                <p>
                  We understand that every traveler is unique, with different preferences, budgets, and dreams. That's why we specialize in creating customized travel packages that perfectly match your needs. From romantic honeymoons to adventurous treks, from spiritual pilgrimages to corporate travel, we handle it all with expertise and care.
                </p>
                <p>
                  Our team of experienced travel consultants works tirelessly to ensure that every aspect of your journey is seamless. We take pride in our attention to detail, competitive pricing, and round-the-clock support that makes us stand out in the industry.
                </p>
              </div>
            </div>
            <div className="relative">
              <div 
                className="h-96 rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary-orange rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Our Mission, Vision & Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const ValueIcon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
                >
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center mb-6">
                    <ValueIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Why Choose Happy Yatra Travels?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We go the extra mile to make your journey unforgettable
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-orange flex items-center justify-center flex-shrink-0">
                      <FeatureIcon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-gray-700 text-lg pt-2">{feature.text}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="bg-gradient-to-br from-primary-blue to-primary-orange p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6">Our Commitment</h3>
              <p className="text-lg leading-relaxed mb-6">
                At Happy Yatra Travels, we believe that every journey should be extraordinary. Our team of travel experts works tirelessly to curate experiences that create lasting memories.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <TargetIcon className="w-6 h-6" />
                  <span>100% Verified Hotels & Services</span>
                </div>
                <div className="flex items-center gap-3">
                  <LockIcon className="w-6 h-6" />
                  <span>Secure & Safe Booking Process</span>
                </div>
                <div className="flex items-center gap-3">
                  <CreditCardIcon className="w-6 h-6" />
                  <span>Multiple Payment Options</span>
                </div>
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-6 h-6" />
                  <span>24/7 Customer Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Get in Touch
            </h2>
            <p className="text-lg text-gray-600">
              We'd love to hear from you and help plan your next adventure
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center mx-auto mb-4">
                <PhoneIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <a href="tel:+917718838808" className="text-primary-blue hover:text-primary-orange font-semibold">
                +91 77188 38808
              </a>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center mx-auto mb-4">
                <EmailIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
              <a href="mailto:info@happyyatratravels.in" className="text-primary-blue hover:text-primary-orange font-semibold">
                info@happyyatratravels.in
              </a>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center mx-auto mb-4">
                <WhatsAppIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <a 
                href="https://wa.me/917718838808" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-blue hover:text-primary-orange font-semibold"
              >
                Chat with Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue to-dark-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let us help you create memories that will last a lifetime
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/917718838808"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <Link
              to="/packages"
              className="px-8 py-4 bg-white text-primary-blue rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              View Packages
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300"
            >
              Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
